import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

const INITIAL_VISIBLE_ROWS = 2;

function normalize(value) {
  return String(value == null ? '' : value).trim();
}

function fullPatientName(patient) {
  if (!patient || typeof patient !== 'object') return '';
  const first = normalize(patient.firstName || patient.first || patient.givenName);
  const last = normalize(patient.lastName || patient.last || patient.familyName);
  const joined = [first, last].filter(Boolean).join(' ');
  return joined || normalize(patient.name || patient.patientName);
}

function formatDob(patient) {
  if (!patient || typeof patient !== 'object') return '';
  return normalize(patient.dob || patient.dateOfBirth || patient.birthDate || patient.patientDateOfBirth);
}

function DiffValue({ label, previous, next }) {
  const prev = normalize(previous);
  const nxt = normalize(next);
  const unchanged = !nxt || prev === nxt;
  return (
    <div className="widget-bulk-diff-line">
      <span className="widget-bulk-diff-label">{label}:</span>
      {unchanged ? (
        <span className="widget-bulk-diff-value">{prev || nxt || '—'}</span>
      ) : (
        <span className="widget-bulk-diff-pair">
          <span className="widget-bulk-diff-prev">{prev || '—'}</span>
          <span className="widget-bulk-diff-arrow" aria-hidden="true">→</span>
          <span className="widget-bulk-diff-next">{nxt}</span>
        </span>
      )}
    </div>
  );
}

function BulkRow({ row, isComplete }) {
  const patientName = fullPatientName(row?.patient) || normalize(row?.patientName);
  const dob = formatDob(row?.patient) || normalize(row?.patientDateOfBirth || row?.dob);
  const previous = row?.previous || {
    type: row?.typeDiff?.previous,
    status: row?.statusDiff?.previous
  };
  const next = row?.next || {
    type: row?.typeDiff?.next,
    status: row?.statusDiff?.next
  };
  return (
    <div
      className={`widget-bulk-row${isComplete ? ' widget-bulk-row-complete' : ''}`}
      role="listitem"
      data-result-status={isComplete ? 'complete' : 'pending'}
    >
      <div className="widget-bulk-row-head">
        <span className="widget-bulk-row-name">{patientName || 'Unknown patient'}</span>
        {isComplete && (
          <span
            className="widget-bulk-row-check"
            aria-label="Updated successfully"
            title="Updated"
          >
            ✓
          </span>
        )}
        {dob && <span className="widget-bulk-row-dob">DOB: {dob}</span>}
      </div>
      <DiffValue label="type" previous={previous.type} next={next.type} />
      <DiffValue label="status" previous={previous.status} next={next.status} />
    </div>
  );
}

/**
 * Bulk update preview — Option B single-card state machine.
 *
 * submissionState transitions (mirrors ConfirmationCard):
 *   idle ──(Confirm)──▶ sending ──(mutation resolves)──▶ confirmed_in_progress
 *                                                │
 *                                                ▼ (bulkFollowUp.status=complete)
 *                                                confirmed_complete
 *                                                ▼ (bulkFollowUp.status=partial|failed)
 *                                                confirmed_failed   ◀── terminal states rehydrate from
 *                                                                    sessionStorage or follow-up rows
 *
 *   idle ──(Cancel)──▶ sending ──▶ discarded
 *
 *   sending ──(mutation throws)──▶ error (returns to interactable idle-like UI)
 *
 * The terminal visuals are derived entirely client-side from this state
 * machine plus `bulkFollowUp` (status + summary) coming from the downstream
 * `order_bulk_result` row — no server-side card mutation required.
 */
function BulkPreview({
  data,
  conversationId,
  messageId,
  hasAssistantFollowUp,
  bulkFollowUp,
  onConfirmationSubmitted,
  readOnly = false,
  headerClass
}) {
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);

  const header = (data && data.header) || {};
  const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data]);
  const rowCount = Number.isFinite(header.rowCount)
    ? header.rowCount
    : (Number.isFinite(data?.total) ? data.total : rows.length);
  const returnedCount = Number.isFinite(header.returnedCount)
    ? header.returnedCount
    : (Number.isFinite(data?.returnedCount) ? data.returnedCount : rows.length);
  const showReturnedCountCaption = readOnly
    && Number.isFinite(returnedCount)
    && Number.isFinite(rowCount)
    && returnedCount !== rowCount;
  const nextType = normalize(header.nextType || data?.next?.type);
  const nextStatus = normalize(header.nextStatus || data?.next?.status);

  const confirm = (data && data.confirm) || (data && data.submit) || {};
  const cancel = (data && data.cancel) || {};
  const terminal = (data && data.terminal) || {};
  const pendingActionId = normalize(
    confirm.action?.payload?.pendingActionId
    || cancel.action?.payload?.pendingActionId
    || data?.pendingActionId
  );

  const [expanded, setExpanded] = useState(false);
  const [submissionState, setSubmissionState] = useState('idle');

  const persistedStateKey = useMemo(() => {
    if (!conversationId || !pendingActionId) return null;
    const suffix = messageId ? `${pendingActionId}:${messageId}` : pendingActionId;
    return `kapsa:bulk-update-state:v1:${conversationId}:${suffix}`;
  }, [conversationId, pendingActionId, messageId]);

  useEffect(() => {
    if (!persistedStateKey || typeof window === 'undefined') return;
    const persisted = window.sessionStorage.getItem(persistedStateKey);
    if (
      persisted === 'confirmed_in_progress'
      || persisted === 'confirmed_complete'
      || persisted === 'confirmed_failed'
      || persisted === 'discarded'
    ) {
      setSubmissionState(persisted);
    }
  }, [persistedStateKey]);

  useEffect(() => {
    if (!persistedStateKey || typeof window === 'undefined') return;
    if (
      submissionState === 'confirmed_in_progress'
      || submissionState === 'confirmed_complete'
      || submissionState === 'confirmed_failed'
      || submissionState === 'discarded'
    ) {
      window.sessionStorage.setItem(persistedStateKey, submissionState);
      return;
    }
    if (submissionState === 'idle' || submissionState === 'error') {
      window.sessionStorage.removeItem(persistedStateKey);
    }
  }, [persistedStateKey, submissionState]);

  const followUpStatus = normalize(bulkFollowUp?.status).toLowerCase();
  const followUpComplete = followUpStatus === 'complete' || (!followUpStatus && !!hasAssistantFollowUp);
  const followUpFailed = followUpStatus === 'partial' || followUpStatus === 'failed';

  useEffect(() => {
    if (submissionState === 'discarded') return;
    if (followUpComplete && submissionState !== 'confirmed_complete') {
      setSubmissionState('confirmed_complete');
      return;
    }
    if (followUpFailed && submissionState !== 'confirmed_failed') {
      setSubmissionState('confirmed_failed');
    }
  }, [submissionState, followUpComplete, followUpFailed]);

  const confirmComplete = submissionState === 'confirmed_complete' || followUpComplete;
  const confirmFailed = submissionState === 'confirmed_failed' || followUpFailed;
  const confirmInFlight =
    submissionState === 'confirmed_in_progress' && !confirmComplete && !confirmFailed;
  const isDiscarded = submissionState === 'discarded';
  const isSending = submissionState === 'sending';

  const hasOverflow = rows.length > INITIAL_VISIBLE_ROWS;
  const visibleRows = hasOverflow && !expanded ? rows.slice(0, INITIAL_VISIBLE_ROWS) : rows;
  const hiddenCount = Math.max(rows.length - INITIAL_VISIBLE_ROWS, 0);

  const defaultHeaderClass = confirmComplete
    ? 'widget-header-success'
    : confirmFailed
      ? 'widget-header-danger'
    : isDiscarded
      ? 'widget-header-muted'
      : 'widget-header-warning';
  const appliedHeaderClass = headerClass || defaultHeaderClass;

  const defaultStatusText = confirmComplete
    ? 'Bulk update complete'
    : confirmFailed
      ? (followUpStatus === 'partial' ? 'Bulk update partially applied' : 'Bulk update failed')
    : isDiscarded
      ? 'Discarded'
      : confirmInFlight
        ? 'Processing...'
        : 'Bulk update preview';
  const statusText = (confirmComplete || confirmFailed || isDiscarded || confirmInFlight)
    ? defaultStatusText
    : (data?.status?.text || defaultStatusText);

  const submitDisabled = readOnly
    || !conversationId
    || !pendingActionId
    || isSending
    || confirmInFlight
    || confirmComplete
    || confirmFailed
    || isDiscarded;
  const cancelDisabled = readOnly
    || !conversationId
    || !pendingActionId
    || isSending
    || confirmInFlight
    || confirmComplete
    || confirmFailed
    || isDiscarded;

  const handleAction = async (actionKind) => {
    if (!pendingActionId) return;
    const content = actionKind === 'confirm'
      ? `/submit-order-bulk-update ${pendingActionId}`
      : `/discard ${pendingActionId}`;
    setSubmissionState('sending');
    try {
      await sendMessage({ variables: { conversationId, content } });
      if (actionKind === 'confirm') {
        setSubmissionState('confirmed_in_progress');
        onConfirmationSubmitted?.();
      } else {
        setSubmissionState('discarded');
      }
    } catch (error) {
      console.error(`Failed to ${actionKind} bulk update:`, error);
      setSubmissionState('error');
    }
  };

  const onConfirm = () => {
    if (submitDisabled) return;
    handleAction('confirm');
  };
  const onCancel = () => {
    if (cancelDisabled) return;
    handleAction('discard');
  };

  const followUpAppliedCount = Number.isFinite(bulkFollowUp?.appliedCount)
    ? bulkFollowUp.appliedCount
    : null;
  const followUpTotalCount = Number.isFinite(bulkFollowUp?.totalCount)
    ? bulkFollowUp.totalCount
    : rowCount;
  const followUpFailedCount = Number.isFinite(bulkFollowUp?.failedCount)
    ? bulkFollowUp.failedCount
    : 0;
  const followUpRolledBackCount = Number.isFinite(bulkFollowUp?.rolledBackCount)
    ? bulkFollowUp.rolledBackCount
    : 0;
  const appliedCount = Number.isFinite(data?.appliedCount)
    ? data.appliedCount
    : (followUpAppliedCount ?? rowCount);
  const failureSummary = normalize(bulkFollowUp?.summary)
    || (
      followUpStatus === 'partial'
        ? 'Some rows were updated, but others failed.'
        : 'No rows were updated. Please review the failure details and retry if needed.'
    );

  return (
    <div
      className={`widget-card widget-card-bulk widget-card-bulk-${confirmComplete ? 'complete' : (confirmFailed ? 'failed' : (isDiscarded ? 'discarded' : 'pending'))}`}
      data-widget-type="order_bulk_update"
      data-submission-state={submissionState}
      data-result-status={confirmComplete ? 'complete' : (confirmFailed ? 'failed' : 'pending')}
    >
      <div className={`widget-card-status-header ${appliedHeaderClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        <div className="widget-bulk-summary">
          <span className="widget-bulk-count">
            {confirmComplete
              ? `${appliedCount} ${appliedCount === 1 ? 'order updated' : 'orders updated'}`
              : confirmFailed
                ? `${followUpAppliedCount ?? 0} of ${followUpTotalCount} ${followUpTotalCount === 1 ? 'order updated' : 'orders updated'}`
              : `${rowCount} ${rowCount === 1 ? 'order' : 'orders'}`}
          </span>
          {nextType && (
            <span className="widget-badge widget-badge-primary" data-next-field="type">
              Type → {nextType}
            </span>
          )}
          {nextStatus && (
            <span className="widget-badge widget-badge-primary" data-next-field="status">
              Status → {nextStatus}
            </span>
          )}
        </div>
        {showReturnedCountCaption && (
          <p className="widget-caption">
            Showing {returnedCount} of {rowCount} {rowCount === 1 ? 'match' : 'matches'}
          </p>
        )}

        <div className="widget-bulk-rows" role="list">
          {visibleRows.map((row, index) => (
            <BulkRow
              key={row?.uidForOrder || `bulk-row-${index}`}
              row={row}
              isComplete={confirmComplete}
            />
          ))}
        </div>

        {hasOverflow && (
          <>
            <hr className="widget-divider" />
            <button
              type="button"
              className="widget-btn widget-btn-primary widget-bulk-expand"
              aria-expanded={expanded}
              onClick={() => setExpanded((prev) => !prev)}
            >
              <span className="widget-bulk-expand-chevron" aria-hidden="true">
                {expanded ? '\u2303' : '\u2304'}
              </span>
              <span>
                {expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
              </span>
            </button>
          </>
        )}

        {confirmFailed && (
          <div className="widget-bulk-failure-block" role="alert">
            <p className="widget-text widget-text-bold widget-bulk-failure-summary">
              {failureSummary}
            </p>
            <p className="widget-caption widget-bulk-failure-counts">
              {followUpFailedCount > 0 ? `${followUpFailedCount} failed` : '0 failed'}
              {followUpRolledBackCount > 0 ? `, ${followUpRolledBackCount} rolled back` : ''}
            </p>
          </div>
        )}

        {!readOnly && confirmComplete && (
          <div className="widget-card-actions">
            <button
              type="button"
              className="widget-btn widget-btn-confirm is-done widget-btn-success"
              disabled
              aria-label="Bulk update succeeded"
              data-action-type="terminal.success"
            >
              <span className="widget-btn-check" aria-hidden="true">✓</span>
              {terminal.label || 'Success'}
            </button>
          </div>
        )}

        {!readOnly && confirmFailed && (
          <div className="widget-card-actions">
            <button
              type="button"
              className="widget-btn widget-btn-cancel is-done widget-btn-danger"
              disabled
              aria-label="Bulk update failed"
              data-action-type="terminal.failed"
            >
              Failed
            </button>
          </div>
        )}

        {!readOnly && isDiscarded && (
          <div className="widget-card-actions">
            <button
              type="button"
              className="widget-btn widget-btn-cancel is-done"
              disabled
              aria-label="Bulk update canceled"
              data-action-type="terminal.discarded"
            >
              Canceled
            </button>
          </div>
        )}

        {!readOnly && !confirmComplete && !confirmFailed && !isDiscarded && (
          <div className="widget-card-actions">
            <button
              type="button"
              className={`widget-btn widget-btn-confirm ${isSending || confirmInFlight ? 'is-loading' : ''}`}
              onClick={onConfirm}
              disabled={submitDisabled}
              data-action-type={confirm.action?.type || 'order.bulk_update_submit'}
              aria-busy={isSending || confirmInFlight}
            >
              {(isSending || confirmInFlight) && (
                <span className="widget-btn-spinner" aria-hidden="true" />
              )}
              {isSending || confirmInFlight
                ? 'Processing...'
                : (confirm.label || `Update ${rowCount} ${rowCount === 1 ? 'order' : 'orders'}`)}
            </button>
            <button
              type="button"
              className="widget-btn widget-btn-cancel"
              onClick={onCancel}
              disabled={cancelDisabled}
              data-action-type={cancel.action?.type || 'record.discard_action'}
            >
              {cancel.label || 'Cancel'}
            </button>
          </div>
        )}

        {submissionState === 'error' && (
          <p className="widget-text widget-bulk-error" role="alert">
            Something went wrong submitting this update. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

function BulkResult({ data }) {
  const result = (data && data.result) || {};
  const statusText = data?.status?.text || 'Bulk update complete';
  const summary = normalize(result.summary) || normalize(data?.summary);
  const appliedCount = Number.isFinite(result.appliedCount) ? result.appliedCount : null;
  const totalCount = Number.isFinite(result.totalCount) ? result.totalCount : null;
  const rollbackErrors = Array.isArray(result.rollbackErrors) ? result.rollbackErrors : [];

  const headerClass = rollbackErrors.length > 0 ? 'widget-header-warning' : 'widget-header-success';

  return (
    <div className="widget-card widget-card-bulk" data-widget-type="order_bulk_result">
      <div className={`widget-card-status-header ${headerClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        {summary && <p className="widget-text widget-text-bold">{summary}</p>}

        {appliedCount !== null && (
          <p className="widget-text">
            Applied {appliedCount}
            {totalCount !== null ? ` of ${totalCount}` : ''}
            {totalCount !== null && totalCount === 1 ? ' row' : ' rows'}
            {totalCount === null ? (appliedCount === 1 ? ' row' : ' rows') : ''}
            .
          </p>
        )}

        {rollbackErrors.length > 0 && (
          <>
            <hr className="widget-divider" />
            <p className="widget-caption widget-text-bold">
              Rollback issues ({rollbackErrors.length})
            </p>
            <ul className="widget-bulk-rollback-list">
              {rollbackErrors.map((error, index) => {
                const uid = normalize(error?.uidForOrder || error?.orderId);
                const message = normalize(error?.message) || 'Manual correction may be required.';
                return (
                  <li key={uid || `rollback-${index}`} className="widget-bulk-rollback-item">
                    <span className="widget-bulk-rollback-uid">{uid || 'Order'}</span>
                    <span className="widget-bulk-rollback-message">{message}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * OrderBulkUpdateCard — renders the bulk-update preview and result widgets.
 *
 * Handles widgetTypes:
 *   order_bulk_update → preview mode; Option B single-card state machine
 *                       morphs Pending → Processing → Complete/Failed/Canceled
 *                       based on local `submissionState` + `bulkFollowUp`.
 *   order_bulk_result → result mode (summary, appliedCount, rollback errors).
 *                       Suppressed from view when it immediately follows a
 *                       bulk update preview, keeping Option B's single-card UX.
 *   order_filter_result → read-only preview for filter-results view.
 */
function OrderBulkUpdateCard({
  data,
  widgetType,
  conversationId,
  messageId,
  hasAssistantFollowUp,
  bulkFollowUp,
  onConfirmationSubmitted
}) {
  if (widgetType === 'order_bulk_result') {
    return <BulkResult data={data || {}} />;
  }
  if (widgetType === 'order_filter_result') {
    return (
      <BulkPreview
        data={data || {}}
        conversationId={conversationId}
        messageId={messageId}
        readOnly
        headerClass="widget-header-success"
      />
    );
  }
  return (
    <BulkPreview
      data={data || {}}
      conversationId={conversationId}
      messageId={messageId}
      hasAssistantFollowUp={hasAssistantFollowUp}
      bulkFollowUp={bulkFollowUp}
      onConfirmationSubmitted={onConfirmationSubmitted}
    />
  );
}

export default OrderBulkUpdateCard;
